import Vue from 'vue';
import { isEqual } from 'lodash';
import { ValidationObserver } from 'vee-validate';
/**
  Renders a Form context, which approximates vee-validate v4 Form
  across Vue 2 and Vue 3 compatibility: https://vee-validate.logaretm.com/v4/api/form
  difference from vee-validate v4 API includes:
      - Form prop "on-submit" does not exist in v4, this will need to be changed on upgrade
        "on-submit" becomes "@submit" in vee-validate v4
      - Field v-slot prop "on" does not exist in v4, this will need to be deleted on upgrade
        all "on" handlers are applied using the "field" slot-prop in vee-validate v4

  Example usage from a parent component:

    <Form
      ref="updateForm"
      v-slot="{ meta: { valid, pending }, isSubmitting }"
      :initial-values="initialValues"
      :on-submit="updateFormHandler"
    >
      <Field
        name="title"
        label="Title"
        rules="required|min:1|max:200"
        v-slot="{ field, on, errors }"
      >
        <label>
          <span>Title: </span>
          <bf-text-input v-bind="field" v-on="on" :errors="errors" placeholder="Enter title..." />
        <label>
      </Field>
      <bf-button
        type="submit"
        :disabled="
          !valid ||     // disable submit button if form validation is invalid (a form field does not pass validation)
          pending ||    // disable submit button if form validation is pending (asynchronous validation rule)
          isSubmitting  // disable submit button if form submit handler is running (asynchronous backend request)
        "
      >
        Update
      </bf-button>
    </Form>

 */
const Form = Vue.extend({
    name: 'Form',
    components: { ValidationObserver },
    inheritAttrs: false,
    props: {
        initialValues: { type: Object, required: false, default: () => ({}) },
        validateOnMount: { type: Boolean, required: false, default: false },
        onSubmit: { type: Function, required: false, default: null },
        // support form validation simple object schema
        // see: https://vee-validate.logaretm.com/v4/guide/components/validation#form-level-validation
        validationSchema: { type: Object, required: false, default: undefined },
    },
    data() {
        return {
            isSubmitting: false,
            currentValues: { ...this.initialValues },
            // store references to child field component methods
            fields: {},
        };
    },
    computed: {
        dirty() {
            return !isEqual(this.currentValues, this.initialValues);
        }
    },
    watch: {
        initialValues(newValues) {
            for (const fieldName of Object.keys(newValues)) {
                // set field initial values
                this.fields[fieldName]?._setInitialValue(newValues[fieldName]);
            }
        },
        validationSchema(validationSchema) {
            // re-validate when form is no longer disabled (eg. a dialog closing click did not close the dialog)
            if (!validationSchema) {
                const formRef = this.$refs.form;
                formRef?.validate({ silent: false });
            }
        }
    },
    created() {
        this.$emit('created', this);
    },
    mounted() {
        const formRef = this.$refs.form;
        if (formRef && this.validateOnMount) {
            // kludge timeout because our custom Field components take a tick to set up
            setTimeout(() => formRef.validate(), 0);
        }
    },
    methods: {
        _getFormValidationSchema() {
            return this.validationSchema;
        },
        // allow Field components to read from parent Form components
        _getInitialValue(fieldName) {
            return this.initialValues[fieldName];
        },
        // allow Field components to write into from parent Form components
        _setCurrentValue(fieldName, value) {
            this.currentValues[fieldName] = value;
            this.fields[fieldName]?._setCurrentValue(this.currentValues[fieldName]);
        },
        _setFieldReference(fieldName, fieldReference) {
            this.fields[fieldName] = fieldReference;
        },
        // recreate vee-validate v4 isSubmitting slotProp state
        async _submitHandler() {
            this.isSubmitting = true;
            try {
                await this.onSubmit?.(this.currentValues);
            }
            catch {
                // parent form should have handled any specific errors
            }
            this.isSubmitting = false;
        },
        // recreate vee-validate v4 resetForm method
        resetForm(newValues) {
            Object.assign(this.currentValues, newValues || this.initialValues);
            // set field values
            for (const fieldName of Object.keys(this.currentValues)) {
                // set field values
                this.fields[fieldName]?._setCurrentValue(this.currentValues[fieldName]);
            }
            // clear field errors
            this.clearErrors();
        },
        // allow setErrors to get passed through
        setErrors(errors) {
            requestAnimationFrame(() => {
                this.$refs.form?.setErrors(errors);
            });
        },
        // clear all field errors
        clearErrors() {
            const errors = Object.fromEntries(Object.entries(this.fields).map(([name]) => [name, []]));
            this.setErrors(errors);
        },
        async isValid() {
            const formRef = this.$refs.form;
            return await formRef?.validate({ silent: false });
        },
        getValues() {
            return { ...this.currentValues };
        },
    }
});
export default Form;
